import { createSignal, Show } from 'solid-js'
import { api } from '../api/client.ts'
import { downloadBlob, exportPng, exportSvg } from '../export.ts'
import { flushCurrent, importProjectFile, project, renameProject, saveState } from '../project.ts'
import { canRedo, canUndo, redo, undo } from '../scene/store.ts'
import { Icon } from './Icon.tsx'

const SAVE_LABELS = {
  clean: 'Saved',
  dirty: 'Unsaved changes',
  saving: 'Saving…',
  error: 'Save failed',
} as const

export function TopBar(props: { onOpenProjects: () => void }) {
  const [busy, setBusy] = createSignal(false)
  let importInput!: HTMLInputElement

  const name = () => project()?.name ?? ''

  const runExport = async (kind: 'png' | 'svg') => {
    setBusy(true)
    try {
      const fileName = name() || 'canvas'
      const wrote = kind === 'png' ? await exportPng(fileName) : await exportSvg(fileName)
      if (!wrote) console.warn('Nothing to export')
    } catch (err) {
      console.error('Export failed', err)
    } finally {
      setBusy(false)
    }
  }

  const downloadBundle = async (id: string, projectName: string) => {
    setBusy(true)
    try {
      // The bundle carries the thumbnail and the latest scene, so write both first.
      await flushCurrent()
      downloadBlob(await api.exportProject(id), `${projectName || 'canvas'}.lxc`)
    } catch (err) {
      console.error('Download failed', err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <header class="topbar">
      <button type="button" class="btn" onClick={props.onOpenProjects}>
        <Icon name="folder" size={16} />
        Projects
      </button>

      <input
        class="project-name"
        value={name()}
        placeholder="Untitled"
        disabled={!project()}
        onChange={(e) => void renameProject(e.currentTarget.value)}
      />

      <span class="save-state" classList={{ error: saveState() === 'error' }}>
        {SAVE_LABELS[saveState()]}
      </span>

      <div class="spacer" />

      <button type="button" class="btn icon-only" title="Undo (Ctrl Z)" disabled={!canUndo()} onClick={undo}>
        <Icon name="undo" size={16} />
      </button>
      <button type="button" class="btn icon-only" title="Redo (Ctrl Shift Z)" disabled={!canRedo()} onClick={redo}>
        <Icon name="redo" size={16} />
      </button>

      <button type="button" class="btn" disabled={busy()} onClick={() => void runExport('png')}>
        <Icon name="image" size={16} />
        PNG
      </button>
      <button type="button" class="btn" disabled={busy()} onClick={() => void runExport('svg')}>
        <Icon name="image" size={16} />
        SVG
      </button>

      <Show when={project()}>
        {(p) => (
          <button
            type="button"
            class="btn"
            disabled={busy()}
            title="Download the .lxc project bundle"
            onClick={() => void downloadBundle(p().id, p().name)}
          >
            <Icon name="download" size={16} />
            .lxc
          </button>
        )}
      </Show>

      <button type="button" class="btn" onClick={() => importInput.click()}>
        Import
      </button>
      <input
        ref={importInput}
        type="file"
        accept=".lxc,application/zip"
        hidden
        onChange={(e) => {
          const file = e.currentTarget.files?.[0]
          e.currentTarget.value = ''
          if (file) void importProjectFile(file).catch((err) => console.error('Import failed', err))
        }}
      />
    </header>
  )
}
